import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { givmoClient, sdk } from '@givmo/api-client';
import type { CharityComplianceListComplianceFilingOptionsData } from '@givmo/api-client';
import { usersGetMeOptions } from '@givmo/api-client/query';
import {
  COMPLIANCE_REMINDERS_PREFERENCE_KEY,
  COMPLIANCE_ROUTES,
  COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION,
  COMPLIANCE_SLUG,
  ComplianceProvider,
  complianceProfileQueryKey,
  nobodyGetsReminders,
  type ComplianceModuleStanding,
  type ComplianceOperations,
  type CompliancePage,
  type ComplianceRemindersHost,
  type ComplianceSignerCandidate,
  type ProfileParameterPage,
} from '@givmo/compliance';
import { resolveDisclosure } from '@givmo/disclosure';
import { PasskeySetupOffer } from '@givmo/passkeys';
import {
  ORG_CONSOLE_PAGE_LIMIT,
  decodeOrgConnectedAi,
  orgMemberName,
  settingsOriginSearchFor,
  useOrgAdminPreferences,
  useOrgMembersExhausted,
  verdictFor,
  type ModuleReadState,
  type OrgAdminPreferences,
  type OrgConnectedAiRow,
  type OrgConsoleMemberRow,
} from '@givmo/org-console';
import { useAuth } from '../../auth/auth-context';
import { useCharityConsole } from '../../console/charity-console-context';
import { SETTINGS_ROUTE } from '../../console/console-routes';
import { settingsHref } from '../../console/settings-origin';
import { useActiveOrg } from '../../org/active-org-context';

/** The picker's generated query (CCM-R1-ADD-SHEET-NEXT-FROM-ENTRY extends it ahead of the regen). */
type FilingOptionsQuery = CharityComplianceListComplianceFilingOptionsData['query'];

/**
 * Translate today's charity-shaped response vocabulary at the host boundary.
 *
 * The shared package owns no class-specific field or enum. This adapter is the one place where the
 * current backend contract is projected into module vocabulary; the inverse request bodies already
 * contain only module fields and need no translation.
 */
export function adaptCharityComplianceResponse(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(adaptCharityComplianceResponse);
  if (typeof value !== 'object' || value === null) return value;

  const record = value as Record<string, unknown>;
  if (Object.hasOwn(record, 'charity_id') && Object.hasOwn(record, 'org_ref')) {
    throw new Error('compliance adapter: response carries both charity_id and org_ref');
  }

  const structuralChildren = new Set([
    'data',
    'obligation',
    'occurrences',
    'events',
    'parameters',
    'parameter',
    'parameter_record',
    'suggestion',
    'recorded_parameters',
    'computed_from',
    'alert_leads',
    // CCM-MOD5B: a vendor record's detail nests the record. Its events' snapshots stay opaque, as
    // a renewal event's do: the package reads no identity off a snapshot.
    'vendor',
  ]);
  const adapted: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(record)) {
    if (key === 'charity_id') {
      adapted.org_ref = entry;
    } else if (key === 'candidate_origin' && entry === 'charity_recorded_import') {
      adapted[key] = 'organization_recorded_import';
    } else if (key === 'parameter_source' && entry === 'charity_recorded') {
      adapted[key] = 'organization_recorded';
    } else if (structuralChildren.has(key)) {
      adapted[key] = adaptCharityComplianceResponse(entry);
    } else {
      // Record text and arbitrary payloads are opaque. In particular, never rewrite note/reason,
      // recorded/amended values, or future extension bags merely because their bytes resemble an
      // enum or identity field.
      adapted[key] = entry;
    }
  }
  return adapted;
}

function pageQuery(page: CompliancePage | undefined) {
  return {
    'page[limit]': page?.limit,
    'page[offset]': page?.offset,
  };
}

function parameterPageQuery(page: ProfileParameterPage | undefined) {
  return {
    ...pageQuery(page),
    include_superseded: page?.includeSuperseded,
  };
}

/**
 * The signed storage PUT, with exactly the headers the package names (`uploadHeaders`: every header
 * the V4 signature binds). One missing, or one more, computes another canonical request, and storage
 * refuses it with a 403.
 */
async function putArtifactBytes(
  uploadUrl: string,
  headers: Record<string, string>,
  file: Blob,
): Promise<void> {
  const response = await fetch(uploadUrl, { method: 'PUT', headers: { ...headers }, body: file });
  if (!response.ok) throw new Error(`The document couldn’t be stored (${response.status}).`);
}

/**
 * The complete current charity adapter over generated operations.
 *
 * Every server call below is a generated SDK operation — generated path/query/body/response types
 * therefore own verb and wire construction. The AI setting's read and change (CCM-R1-AI-SETTING)
 * graduated from the configured client when the snapshot refresh at backend `f134e3a05`
 * (CCM-R1-INTAKE (a)) generated their routes; their tolerant decode stays in the package
 * (`decodeComplianceAiSetting`). The only request outside the Givmo API client is the signed
 * storage PUT.
 */
export const charityComplianceOperations: ComplianceOperations = {
  listObligations: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceObligations({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getObligation: async (orgRef, obligationId) => {
    const { data } = await sdk.charityComplianceGetComplianceObligation({
      path: { charity_id: orgRef, obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── A filing's rule change, and its re-pin (CCM-OPS1-REPIN, be#2770) ─────────────────────────
  getObligationRuleChange: async (orgRef, obligationId) => {
    const { data } = await sdk.charityComplianceGetComplianceObligationRuleChange({
      path: { charity_id: orgRef, obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  repinObligation: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceRepinComplianceObligation({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── Stopping and restarting a filing's tracking (CCM-R1-TRACKING-PANEL, be#2811) ──────────────
  stopTracking: async (orgRef, obligationId) => {
    const { data } = await sdk.charityComplianceStopTrackingComplianceObligation({
      path: { charity_id: orgRef, obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  resumeTracking: async (orgRef, obligationId) => {
    const { data } = await sdk.charityComplianceResumeTrackingComplianceObligation({
      path: { charity_id: orgRef, obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listOccurrences: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceOccurrences({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordSubmission: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceSubmission({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordWindowClosed: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceWindowClosed({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordRegistrationDate: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceRegistrationDate({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordFiling: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceFiling({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  issueArtifactUploadUrl: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceIssueComplianceArtifactUploadUrl({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  confirmArtifact: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceConfirmComplianceArtifact({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listArtifacts: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceArtifacts({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listObligationArtifacts: async (orgRef, obligationId) => {
    const { data } = await sdk.charityComplianceListComplianceObligationArtifacts({
      path: { charity_id: orgRef, obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getArtifactDownload: async (orgRef, artifactId) => {
    const { data } = await sdk.charityComplianceGetComplianceArtifactDownload({
      path: { charity_id: orgRef, artifact_id: artifactId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  deleteArtifact: async (orgRef, artifactId) => {
    await sdk.charityComplianceDeleteComplianceArtifact({
      path: { charity_id: orgRef, artifact_id: artifactId },
      throwOnError: true,
    });
  },
  getProfile: async (orgRef) => {
    const { data } = await sdk.charityComplianceGetComplianceProfile({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listProfileParameters: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceProfileParameters({
      path: { charity_id: orgRef },
      query: parameterPageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordProfileParameter: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceProfileParameter({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listProfileSuggestions: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceProfileSuggestions({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  resolveProfileSuggestion: async (orgRef, suggestionId, body) => {
    const { data } = await sdk.charityComplianceResolveComplianceProfileSuggestion({
      path: { charity_id: orgRef, suggestion_id: suggestionId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listProfileReviewFlags: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceProfileReviewFlags({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  resolveProfileReviewFlag: async (orgRef, reviewFlagId, body) => {
    const { data } = await sdk.charityComplianceResolveComplianceProfileReviewFlag({
      path: { charity_id: orgRef, review_flag_id: reviewFlagId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listRegistrationImports: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceRegistrationImports({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  importRegistration: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceImportComplianceRegistration({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  amendRegistrationImport: async (orgRef, importId, body) => {
    const { data } = await sdk.charityComplianceAmendComplianceRegistrationImport({
      path: { charity_id: orgRef, import_id: importId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listRegistrationImportAmendments: async (orgRef, importId) => {
    const { data } = await sdk.charityComplianceListComplianceRegistrationImportAmendments({
      path: { charity_id: orgRef, import_id: importId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── The organization's registered agents and foreign qualifications (CCM-MOD5B) ─────────────
  listVendorRecords: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceVendors({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getVendorRecord: async (orgRef, vendorId) => {
    const { data } = await sdk.charityComplianceGetComplianceVendor({
      path: { charity_id: orgRef, vendor_id: vendorId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordVendor: async (orgRef, vendorId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceVendor({
      path: { charity_id: orgRef, vendor_id: vendorId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  linkVendorRecord: async (orgRef, vendorId, obligationId) => {
    const { data } = await sdk.charityComplianceLinkComplianceVendorFiling({
      path: { charity_id: orgRef, vendor_id: vendorId },
      body: { obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  unlinkVendorRecord: async (orgRef, vendorId, obligationId) => {
    const { data } = await sdk.charityComplianceUnlinkComplianceVendorFiling({
      path: { charity_id: orgRef, vendor_id: vendorId },
      body: { obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getAiSetting: async (orgRef) => {
    const { data } = await sdk.charityComplianceGetComplianceAiSetting({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  setAiSetting: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceSetComplianceAiSetting({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listFilingOptions: async (orgRef, page, jurisdictionCode, lastFilingOn, filedFor) => {
    // A typed const, so `last_filing_on` (CCM-R1-ADD-SHEET-NEXT-FROM-ENTRY, ahead of the regen) rides
    // the generated query (`agent_docs/api-client.md`); a backend without it ignores it. So do the
    // year named for a late one, as a pair (review-191's F5).
    const query: NonNullable<FilingOptionsQuery> & {
      last_filing_on?: string;
      filed_for_cycle_key?: string;
      filed_for_deadline_index?: number;
    } = {
      ...pageQuery(page),
      // Omitted, not blank, when no jurisdiction is chosen: a present-and-empty code is a 422.
      ...(jurisdictionCode !== undefined && jurisdictionCode.length > 0
        ? { jurisdiction_code: jurisdictionCode }
        : {}),
      ...(lastFilingOn !== undefined ? { last_filing_on: lastFilingOn } : {}),
      ...(lastFilingOn !== undefined && filedFor !== undefined
        ? {
            filed_for_cycle_key: filedFor.cycle_key,
            filed_for_deadline_index: filedFor.deadline_index,
          }
        : {}),
    };
    const { data } = await sdk.charityComplianceListComplianceFilingOptions({
      path: { charity_id: orgRef },
      query,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listFilingGroups: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceFilingGroups({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  createFilingGroup: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceCreateComplianceFilingGroup({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  renameFilingGroup: async (orgRef, groupId, body) => {
    const { data } = await sdk.charityComplianceRenameComplianceFilingGroup({
      path: { charity_id: orgRef, group_id: groupId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  moveFilingGroup: async (orgRef, groupId, body) => {
    const { data } = await sdk.charityComplianceMoveComplianceFilingGroup({
      path: { charity_id: orgRef, group_id: groupId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  removeFilingGroup: async (orgRef, groupId) => {
    await sdk.charityComplianceRemoveComplianceFilingGroup({
      path: { charity_id: orgRef, group_id: groupId },
      throwOnError: true,
    });
  },
  moveFilingToGroup: async (orgRef, groupId, body) => {
    const { data } = await sdk.charityComplianceMoveComplianceFilingToGroup({
      path: { charity_id: orgRef, group_id: groupId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listOrganizationFilings: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceOrganizationFilings({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getOrganizationFiling: async (orgRef, filingId) => {
    const { data } = await sdk.charityComplianceGetComplianceOrganizationFiling({
      path: { charity_id: orgRef, filing_id: filingId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  addOrganizationFiling: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceAddComplianceOrganizationFiling({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  updateOrganizationFiling: async (orgRef, filingId, body) => {
    const { data } = await sdk.charityComplianceUpdateComplianceOrganizationFiling({
      path: { charity_id: orgRef, filing_id: filingId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordOrganizationFilingFiled: async (orgRef, filingId, cycleId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceOrganizationFilingFiled({
      path: { charity_id: orgRef, filing_id: filingId, cycle_id: cycleId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordOrganizationFilingSent: async (orgRef, filingId, cycleId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceOrganizationFilingSent({
      path: { charity_id: orgRef, filing_id: filingId, cycle_id: cycleId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  addOrganizationFilingCycle: async (orgRef, filingId, body) => {
    const { data } = await sdk.charityComplianceAddComplianceOrganizationFilingCycle({
      path: { charity_id: orgRef, filing_id: filingId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listRenewalCycles: async (orgRef, page, obligationId) => {
    const { data } = await sdk.charityComplianceListComplianceRenewalCycles({
      path: { charity_id: orgRef },
      query: {
        ...pageQuery(page),
        ...(obligationId !== undefined ? { obligation_id: obligationId } : {}),
      },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordRenewalFiling: async (orgRef, cycleId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceRenewalFiling({
      path: { charity_id: orgRef, cycle_id: cycleId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordRenewalSubmission: async (orgRef, cycleId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceRenewalSubmission({
      path: { charity_id: orgRef, cycle_id: cycleId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  attachRenewalFiledCopy: async (orgRef, cycleId, body) => {
    const { data } = await sdk.charityComplianceAttachComplianceRenewalFiledCopy({
      path: { charity_id: orgRef, cycle_id: cycleId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  addRenewalAttachment: async (orgRef, cycleId, body) => {
    const { data } = await sdk.charityComplianceAddComplianceRenewalAttachment({
      path: { charity_id: orgRef, cycle_id: cycleId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  attachObligationFiledCopy: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceAttachComplianceObligationFiledCopy({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  addObligationAttachment: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceAddComplianceObligationAttachment({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  addEarlierRenewalCycle: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceAddComplianceEarlierRenewalCycle({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── Preparing a filing's form (CCM-R1-DOCS; CCM-R1-DOCS-READS) ─────────────────────────────
  listFormAdapters: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceFormAdapters({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listFilingCases: async (orgRef, obligationId, page) => {
    const { data } = await sdk.charityComplianceListComplianceFilingCases({
      path: { charity_id: orgRef },
      query: { ...pageQuery(page), obligation_id: obligationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  openFilingCase: async (orgRef, obligationId, body) => {
    const { data } = await sdk.charityComplianceOpenComplianceFilingCase({
      path: { charity_id: orgRef, obligation_id: obligationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listCasePreparations: async (orgRef, caseId, page) => {
    const { data } = await sdk.charityComplianceListComplianceFormPreparations({
      path: { charity_id: orgRef, case_id: caseId },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  startFormPreparation: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceStartComplianceFormPreparation({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getFormPreparation: async (orgRef, preparationId) => {
    const { data } = await sdk.charityComplianceGetComplianceFormPreparation({
      path: { charity_id: orgRef, preparation_id: preparationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  answerFormItem: async (orgRef, preparationId, itemKey, body) => {
    const { data } = await sdk.charityComplianceAnswerComplianceFormItem({
      path: { charity_id: orgRef, preparation_id: preparationId, item_key: itemKey },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // The page is a PNG drawn per request (`private, no-store`): read as bytes, never as JSON. Its
  // signal aborts the request when the page goes (CCM-R1-WORKBENCH-PREVIEW-SUPERSEDED).
  getFormPagePreview: async (orgRef, preparationId, page, options) => {
    const { data } = await sdk.charityComplianceGetComplianceFormPagePreview({
      path: { charity_id: orgRef, preparation_id: preparationId, page },
      parseAs: 'blob',
      throwOnError: true,
      ...(options?.signal !== undefined ? { signal: options.signal } : {}),
    });
    return data as unknown as Blob;
  },
  recordFormVersion: async (orgRef, preparationId, body) => {
    const { data } = await sdk.charityComplianceMintComplianceFormVersion({
      path: { charity_id: orgRef, preparation_id: preparationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getDocumentVersionDownload: async (orgRef, documentId, versionId) => {
    const { data } = await sdk.charityComplianceGetComplianceFilingDocumentVersionDownload({
      path: { charity_id: orgRef, document_id: documentId, version_id: versionId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getFilingCaseChecklist: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceGetComplianceFilingCaseChecklist({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  attachChecklistDocument: async (orgRef, caseId, attachmentIndex, body) => {
    const { data } = await sdk.charityComplianceAttachComplianceChecklistDocument({
      path: { charity_id: orgRef, case_id: caseId, attachment_index: attachmentIndex },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordChecklistAct: async (orgRef, caseId, attachmentIndex, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceChecklistAct({
      path: { charity_id: orgRef, case_id: caseId, attachment_index: attachmentIndex },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listCaseSigningRequests: async (orgRef, caseId, page) => {
    const { data } = await sdk.charityComplianceListComplianceSigningRequests({
      path: { charity_id: orgRef },
      query: { ...pageQuery(page), filing_case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getSigningRequest: async (orgRef, requestId) => {
    const { data } = await sdk.charityComplianceGetComplianceSigningRequest({
      path: { charity_id: orgRef, request_id: requestId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── Signing a request (CCM-R1-SIGN-WEB, the signer's side) ─────────────────────────────────
  getFilingCase: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceGetComplianceFilingCase({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getFilingDocument: async (orgRef, documentId) => {
    const { data } = await sdk.charityComplianceGetComplianceFilingDocument({
      path: { charity_id: orgRef, document_id: documentId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getDocumentVersionRendition: async (orgRef, documentId, versionId) => {
    const { data } = await sdk.charityComplianceGetComplianceFilingDocumentVersionRendition({
      path: { charity_id: orgRef, document_id: documentId, version_id: versionId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  signSigningRequest: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceSignComplianceSigningRequest({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  declineSigningRequest: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceDeclineComplianceSigningRequest({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  withdrawSignature: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceWithdrawComplianceSignature({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getSigningSetting: async (orgRef) => {
    const { data } = await sdk.charityComplianceGetComplianceSigningSetting({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  setSigningSetting: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceSetComplianceSigningSetting({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── Sending a request (CCM-R1-SIGN-WEB-B, the issuer's side) ───────────────────────────────
  getSigningPlaces: async (orgRef, documentId, versionId) => {
    const { data } = await sdk.charityComplianceGetComplianceFilingDocumentSigningPlaces({
      path: { charity_id: orgRef, document_id: documentId, version_id: versionId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  issueSigningRequest: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceIssueComplianceSigningRequest({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  reviseSigningRequest: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceReviseComplianceSigningRequest({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  extendSigningRequest: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceExtendComplianceSigningRequest({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  cancelSigningRequest: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceCancelComplianceSigningRequest({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getSigningRecordLines: async (orgRef, requestId) => {
    const { data } = await sdk.charityComplianceGetComplianceSigningRecordLines({
      path: { charity_id: orgRef, request_id: requestId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getSigningEvidence: async (orgRef, requestId, format) => {
    const { data } = await sdk.charityComplianceGetComplianceSigningEvidence({
      path: { charity_id: orgRef, request_id: requestId },
      query: { format },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  removeSigningIdentity: async (orgRef, requestId, body) => {
    const { data } = await sdk.charityComplianceRemoveComplianceSigningIdentity({
      path: { charity_id: orgRef, request_id: requestId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listSigningPeople: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceSigningPeople({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getSigningExport: async (orgRef, requestId) => {
    const { data } = await sdk.charityComplianceGetComplianceSigningExport({
      path: { charity_id: orgRef, request_id: requestId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getRenewalCycle: async (orgRef, cycleId) => {
    const { data } = await sdk.charityComplianceGetComplianceRenewalCycle({
      path: { charity_id: orgRef, cycle_id: cycleId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── Sending a filing (CCM-R1-SUBMIT-WEB) ─────────────────────────────────────────────────
  getSubmission: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceGetComplianceSubmission({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  authorizeSubmission: async (orgRef, caseId, body) => {
    const { data } = await sdk.charityComplianceAuthorizeComplianceSubmission({
      path: { charity_id: orgRef, case_id: caseId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  revokeSubmissionAuthorization: async (orgRef, authorizationId) => {
    const { data } = await sdk.charityComplianceRevokeComplianceSubmissionAuthorization({
      path: { charity_id: orgRef, authorization_id: authorizationId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  dispatchSubmission: async (orgRef, authorizationId, body) => {
    const { data } = await sdk.charityComplianceDispatchComplianceSubmission({
      path: { charity_id: orgRef, authorization_id: authorizationId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  reconcileSubmissionAttempt: async (orgRef, attemptId, body) => {
    const { data } = await sdk.charityComplianceReconcileComplianceSubmissionAttempt({
      path: { charity_id: orgRef, attempt_id: attemptId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getSubmissionPackage: async (orgRef, caseId, packageSha256) => {
    const { data } = await sdk.charityComplianceGetComplianceSubmissionPackage({
      path: { charity_id: orgRef, case_id: caseId },
      query: { package_sha256: packageSha256 },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  recordSubmissionHandoff: async (orgRef, caseId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceSubmissionHandoff({
      path: { charity_id: orgRef, case_id: caseId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // CCM-R1-RESULTS-WEB: what came back after a send.
  recordSubmissionEvidence: async (orgRef, caseId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceSubmissionEvidence({
      path: { charity_id: orgRef, case_id: caseId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  answerSubmissionEvidence: async (orgRef, caseId, evidenceId, body) => {
    const { data } = await sdk.charityComplianceAnswerComplianceSubmissionEvidence({
      path: { charity_id: orgRef, case_id: caseId, evidence_id: evidenceId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listSubmissionFollowThrough: async (orgRef, page) => {
    const { data } = await sdk.charityComplianceListComplianceSubmissionFollowThrough({
      path: { charity_id: orgRef },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // CCM-R1-DESK-WEB: Givmo's filing desk.
  handFilingToDesk: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceHandComplianceFilingToDesk({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  takeFilingFromDesk: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceTakeComplianceFilingFromDesk({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  resumeDesk: async (orgRef, caseId) => {
    const { data } = await sdk.charityComplianceResumeComplianceDesk({
      path: { charity_id: orgRef, case_id: caseId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getDeskStanding: async (orgRef) => {
    const { data } = await sdk.charityComplianceGetComplianceDeskAuthorization({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  revokeDeskStanding: async (orgRef) => {
    const { data } = await sdk.charityComplianceRevokeComplianceDeskAuthorization({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // CCM-R1-SIGN-OUTSIDE-B's act, graduated to its SDK op at the snapshot refresh off backend
  // `8a9cc0693` (cut 1008-15).
  recordExecutedCopy: async (orgRef, documentId, versionId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceExecutedCopy({
      path: { charity_id: orgRef, document_id: documentId, version_id: versionId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── The review (CCM-R1-REVIEW-WEB) ─────────────────────────────────────────────────────────
  getProposal: async (orgRef) => {
    const { data } = await sdk.charityComplianceGetComplianceProposal({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  generateProposalRevision: async (orgRef) => {
    const { data } = await sdk.charityComplianceGenerateComplianceProposalRevision({
      path: { charity_id: orgRef },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getProposalRevision: async (orgRef, revisionId) => {
    const { data } = await sdk.charityComplianceGetComplianceProposalRevision({
      path: { charity_id: orgRef, revision_id: revisionId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listProposalItems: async (orgRef, revisionId, page) => {
    const { data } = await sdk.charityComplianceListComplianceProposalItems({
      path: { charity_id: orgRef, revision_id: revisionId },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listProposalResults: async (orgRef, revisionId, page) => {
    const { data } = await sdk.charityComplianceListComplianceProposalResults({
      path: { charity_id: orgRef, revision_id: revisionId },
      query: pageQuery(page),
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // The review's two routes (be#2710), graduated to their SDK ops at the snapshot refresh off
  // backend `8a9cc0693` (cut 1008-15).
  recordProposalReview: async (orgRef, revisionId, body) => {
    const { data } = await sdk.charityComplianceRecordComplianceProposalReview({
      path: { charity_id: orgRef, revision_id: revisionId },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  getProposalReview: async (orgRef, revisionId) => {
    const { data } = await sdk.charityComplianceGetComplianceProposalReview({
      path: { charity_id: orgRef, revision_id: revisionId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  // ── Asking a teammate (CCM-R1-REVIEW-ASSIGN, be#2776) ──────────────────────────────────────────
  askAboutReviewItem: async (orgRef, body) => {
    const { data } = await sdk.charityComplianceAskAboutComplianceReviewItem({
      path: { charity_id: orgRef },
      body,
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  withdrawReviewAsk: async (orgRef, askId) => {
    const { data } = await sdk.charityComplianceWithdrawComplianceReviewAsk({
      path: { charity_id: orgRef, ask_id: askId },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  listReviewAsks: async (orgRef, filter, page) => {
    const { data } = await sdk.charityComplianceListComplianceReviewAsks({
      path: { charity_id: orgRef },
      query: {
        ...pageQuery(page),
        ...(filter.askedUserId !== undefined ? { asked_user_id: filter.askedUserId } : {}),
        ...(filter.state !== undefined ? { state: filter.state } : {}),
      },
      throwOnError: true,
    });
    return adaptCharityComplianceResponse(data);
  },
  putArtifactBytes,
  resolveDisclosure: async (registrySurface) => {
    const resolved = await resolveDisclosure({ client: givmoClient, surface: registrySurface });
    return resolved?.lines ?? null;
  },
};

/**
 * The module's D-CPO-24 standing, derived ONCE from the console's standing read — the same three
 * signals the direct-link gate consumes, so the gate and the package can never disagree.
 *
 * | the read says                              | standing        |
 * | ------------------------------------------ | --------------- |
 * | pending, or errored (the gate blocks both) | `'unknown'`     |
 * | reported: held and on (`link`)             | `'available'`   |
 * | reported: anything else                    | `'unavailable'` |
 * | settled, NOTHING reported                  | `'available'`   |
 *
 * ⚠ THE LAST ROW IS THE HOST'S PERMISSIVE POSTURE, DECIDED HERE AND NEVER IN THE PACKAGE. A settled
 * read that reports no standings is the console being dark (`ORG_CONSOLE_ENABLED` unset — a 404,
 * `isUnavailable`) or a backend predating the fields; shell contract v3 answers `link` for it
 * everywhere (the tile, the row, the gate — "the module's own reads answer for themselves behind
 * this"), and the server's own authorization is what refuses a non-holder. Mapping it to
 * `'unknown'` would black out the module for a Global Admin because the console read was dark,
 * which the contract calls a worse answer than a page whose reads refuse. The package itself stays
 * strict: it fires nothing unless told `'available'`, and the two REPORTED refusals — the no-grant
 * victim and the revoked-mid-session victim — reach it as `'unavailable'`.
 */
export function complianceStandingFor(read: ModuleReadState): ComplianceModuleStanding {
  if (read.pending || read.errored) return 'unknown';
  if (read.standings === null) return 'available';
  return verdictFor(read.standings, COMPLIANCE_SLUG) === 'link' ? 'available' : 'unavailable';
}

/** The Notifications card's own anchor on the Settings page (`OrgAdminPreferencesPanel`). */
export const NOTIFICATIONS_ANCHOR = 'notifications';

/**
 * Whether a preferences read serves the reminders switch: `undefined` until it has answered; `false`
 * for a failed read, whose honest words are the Member's — they promise no switch.
 */
export function remindersSwitchServed(
  data: OrgAdminPreferences | null | undefined,
  isError: boolean,
): boolean | undefined {
  if (data === undefined) return isError ? false : undefined;
  return (
    data?.definitions.some(
      (definition) => definition.key === COMPLIANCE_REMINDERS_PREFERENCE_KEY,
    ) === true
  );
}

/** The switch's address from `from`: the gear's own, plus the Notifications card's anchor. */
export function notificationSettingsHref(from: string): string {
  return `${settingsHref(from, COMPLIANCE_SLUG)}#${NOTIFICATIONS_ANCHOR}`;
}

/**
 * The viewer's own "Email me Compliance reminders" switch, answered for the package's notice for
 * when nobody gets deadline reminders (CCM-R1-REMIND-WEB; D-CCM-18). Two answers the package cannot
 * give itself, because both are the console's:
 *
 *   1. WHETHER the viewer is served the switch. The backend serves `compliance.reminders.email` to
 *      exactly the people reminders go to (Compliance Owners and Global Admins), so the viewer's own
 *      preferences read is the Owner-or-Member answer. ⚠ It is the ORG CONSOLE's read, on the key the
 *      Settings page draws the switch from: one cache entry and one decode, and the Notifications
 *      card is already drawn when the link lands on it. Taken only while the module is available
 *      AND its profile says nobody gets reminders (the REMIND-WEB review P1: lazily, never on every
 *      page). The host does not fetch the profile: it listens on the package's own read (the same
 *      key, `enabled: false`), so a page that never asks for the profile never causes this read.
 *   2. WHERE the switch lives: Settings › Your settings › Notifications, opened the way the gear
 *      opens it from here — this pathname and Compliance's frame in the page's address (Org
 *      Machinery OM-28), so the page puts Compliance's section first — plus the card's anchor.
 */
function useComplianceReminders(
  orgRef: string | null,
  available: boolean,
): ComplianceRemindersHost {
  const profile = useQuery({
    queryKey: complianceProfileQueryKey(orgRef),
    queryFn: () => charityComplianceOperations.getProfile(orgRef ?? ''),
    enabled: false,
  });
  const read = useOrgAdminPreferences(
    orgRef ?? '',
    available && orgRef !== null && nobodyGetsReminders(profile.data),
  );
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const switchServed = remindersSwitchServed(read.data, read.isError);
  return useMemo(
    () => ({
      switchServed,
      settings: {
        href: notificationSettingsHref(pathname),
        open: () =>
          void navigate({
            to: SETTINGS_ROUTE,
            search: settingsOriginSearchFor(pathname, COMPLIANCE_SLUG),
            hash: NOTIFICATIONS_ANCHOR,
          }),
      },
    }),
    [switchServed, pathname, navigate],
  );
}

/**
 * The console-fed host: `orgRef` and readiness from the active-org context (the memberships read,
 * fail-closed until an organization resolves — the same source the Books mount reads),
 * `moduleStanding` from the console's ONE standing read, and the viewer's own reminders switch
 * (above). Rendered only inside `CharityComplianceMount`'s gate; the one read it opens is the
 * viewer's own preferences, the org console's, and only while the module is available.
 */
/**
 * The module's write string on the charity catalog. Filings offers its organizing changes only to a
 * holder (DESIGN-NOTE-1b §2.4); the server refuses everyone else whatever the page offers.
 */
export const CHARITY_COMPLIANCE_WRITE_PERMISSION = 'charity_compliance:write';

/**
 * Whether the viewer may write, from the console's ONE standing read: a REPORTED effective set
 * decides; nothing reported is the permissive posture the standing mapper takes too (the server
 * still gates every write).
 */
export function complianceCanWrite(permissions: readonly string[] | null): boolean {
  return permissions === null || permissions.includes(CHARITY_COMPLIANCE_WRITE_PERMISSION);
}

/**
 * A person's name from the organization's roster, for "Added by …" on a filing's years. Any admin
 * may read the roster ("seeing who else administers your own organization is not privileged"); a
 * person it does not list, or a row with no name, is unnamed — the line then says "Added on …".
 */
export function rosterNames(
  rows: readonly OrgConsoleMemberRow[] | undefined,
): (userId: string) => string | undefined {
  const names = new Map<string, string>();
  for (const row of rows ?? []) {
    const name = [row.first_name, row.last_name]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .map((part) => part.trim())
      .join(' ');
    if (name.length > 0) names.set(row.user_id, name);
  }
  return (userId) => names.get(userId);
}

/**
 * Everyone on the roster, each named as the Team page names them (`orgMemberName`: their name, else
 * their email): for the removal of sign-in details, which calls someone "no longer in your
 * organization" only when the roster doesn't list them (CCM-R1-SIGN-REDACT-EARLIER-SIGNERS; cut
 * 1008-105's review, F2). `undefined` while the roster reads, `null` when its read failed.
 */
export function organizationMembers(roster: {
  data?: readonly OrgConsoleMemberRow[] | undefined;
  isError: boolean;
}): { userId: string; name: string }[] | null | undefined {
  if (roster.data !== undefined) {
    return roster.data.map((row) => ({ userId: row.user_id, name: orgMemberName(row) }));
  }
  return roster.isError ? null : undefined;
}

/**
 * Whether the viewer may send documents for signature (CCM-R1-SIGN-WEB-B): the module's D-SIGN-2
 * string in the console's ONE standing read. Nothing reported answers nothing, so the package never
 * shows its Owner line on a guess (the server gates the send whatever this says).
 */
export function complianceCanSendForSignature(
  permissions: readonly string[] | null,
): boolean | undefined {
  return permissions === null
    ? undefined
    : permissions.includes(COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION);
}

/** The module's invite string (D-SIGN-7), released with signing: who the send sheet's invite serves. */
export const COMPLIANCE_INVITE_ADMINS_PERMISSION = 'compliance:invite_admins';

/**
 * The people the send sheet can name to sign: everyone the roster says holds Compliance (a Global
 * Admin holds every module by tier), named by first and last name, else their email. `undefined`
 * while the roster reads.
 */
export function complianceSignerCandidates(
  rows: readonly OrgConsoleMemberRow[] | undefined,
): ComplianceSignerCandidate[] | undefined {
  if (rows === undefined) return undefined;
  return rows.flatMap((row) => {
    if (row.modules.find((module) => module.module === COMPLIANCE_SLUG)?.has_access !== true) {
      return [];
    }
    const name = [row.first_name, row.last_name]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .map((part) => part.trim())
      .join(' ');
    const email = row.email?.trim() ?? '';
    const shown = name.length > 0 ? name : email;
    if (shown.length === 0) return [];
    return [{ userId: row.user_id, name: shown, ...(email.length > 0 ? { email } : {}) }];
  });
}

/**
 * The people who can authorize sending a filing (CCM-R1-SUBMIT-WEB; frame 71's "At {org}, that's
 * …"): the roster's rows whose Compliance permissions hold the send string (a Compliance Owner, or
 * a Global Admin by tier), named as the send sheet names its signers. `undefined` while the roster
 * reads.
 */
export function complianceAuthorizers(
  rows: readonly OrgConsoleMemberRow[] | undefined,
): { userId: string; name: string }[] | undefined {
  if (rows === undefined) return undefined;
  const holders = rows.filter((row) =>
    row.modules
      .find((module) => module.module === COMPLIANCE_SLUG)
      ?.effective_permissions.includes(COMPLIANCE_SEND_FOR_SIGNATURE_PERMISSION),
  );
  return (complianceSignerCandidates(holders) ?? []).map(({ userId, name }) => ({ userId, name }));
}

/**
 * The people "Ask a teammate" may ask (CCM-R1-REVIEW-ASSIGN; N42's access line): the roster's rows
 * whose Compliance permissions hold the module's write string, named as the send sheet names its
 * signers. The server resolves it again at the ask and refuses anyone else. `undefined` while the
 * roster reads.
 */
export function complianceWriters(
  rows: readonly OrgConsoleMemberRow[] | undefined,
): { userId: string; name: string }[] | undefined {
  if (rows === undefined) return undefined;
  const writers = rows.filter((row) =>
    row.modules
      .find((module) => module.module === COMPLIANCE_SLUG)
      ?.effective_permissions.includes(CHARITY_COMPLIANCE_WRITE_PERMISSION),
  );
  return (complianceSignerCandidates(writers) ?? []).map(({ userId, name }) => ({ userId, name }));
}

/** The consent-time scope a connected AI needs to write Compliance's records (CCM-R1-SUBMIT-MCP). */
export const COMPLIANCE_WRITE_SCOPE = 'org.compliance.write';

/**
 * Whether the viewer has an AI connected to this organization that may write its Compliance
 * records (CCM-R1-SUBMIT-MCP; UX slice 3's P6): a connected-AI grant acting as the viewer whose
 * consent-time scopes include the Compliance write scope. Never Givmo's own AI setting. `undefined`
 * until both the list and the viewer's backend id are known — the roster's id-space, never the
 * Firebase uid.
 */
export function connectedAiCanSend(
  rows: readonly OrgConnectedAiRow[] | undefined,
  viewerId: string | undefined,
): boolean | undefined {
  if (rows === undefined || viewerId === undefined) return undefined;
  return rows.some(
    (row) => row.acting_admin_id === viewerId && row.scopes.includes(COMPLIANCE_WRITE_SCOPE),
  );
}

/**
 * Every page of the organization's connected AIs (the org console's own read, which any admin may
 * make), only once Compliance is available here. A refusal (the console dark, say) leaves the fact
 * unknown, so the offer doesn't show; it is never retried into a guess.
 */
function useConnectedAiRows(orgRef: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['compliance', 'connected-ai', orgRef],
    enabled: enabled && orgRef !== null,
    retry: false,
    queryFn: async ({ signal }) => {
      const rows: OrgConnectedAiRow[] = [];
      for (let offset = 0; ; offset += ORG_CONSOLE_PAGE_LIMIT) {
        const { data } = await sdk.orgConsoleListOrgConnectedAi({
          path: { org_ref: orgRef ?? '' },
          query: { 'page[limit]': ORG_CONSOLE_PAGE_LIMIT, 'page[offset]': offset },
          signal,
          throwOnError: true,
        });
        const page = decodeOrgConnectedAi(data);
        rows.push(...page);
        if (page.length < ORG_CONSOLE_PAGE_LIMIT) return rows;
      }
    },
  });
}

/** The offer's variant for each act that opens it: its "Not now" line names the act. */
const SETUP_OFFER_VARIANT = {
  sign: 'from-sign',
  send: 'from-send',
  level: 'from-level',
} as const;

interface SetupOfferProps {
  act: 'sign' | 'send' | 'level';
  onSettled: (outcome: 'created' | 'declined' | 'unavailable') => void;
}

/**
 * The passkey setup offer Compliance's Sign, its Authorize and send (CCM-R1-SIGN-PASSKEY), or its
 * sign-in check card's Save (Account Security's AS-PWL-2e) opens when the sign-in check can't be met
 * here: Account Security's card, its "Not now" line naming the act. The Settings page's Compliance
 * section hands the same one to the card (`compliance-settings.tsx`).
 */
export function renderSetupOffer(props: SetupOfferProps) {
  return <CharitySetupOffer {...props} />;
}

/**
 * The offer for the signed-in account, so it can tell whether a sign-in lock binds it (AS-WEB23):
 * under one it sets nothing up and says why itself, since Compliance places no words above it.
 */
function CharitySetupOffer({ act, onSettled }: SetupOfferProps) {
  const { user } = useAuth();
  return (
    <PasskeySetupOffer
      variant={SETUP_OFFER_VARIANT[act]}
      onSettled={onSettled}
      uid={user?.uid ?? null}
    />
  );
}

export function CharityComplianceHost({ children }: { children: ReactNode }) {
  const { activeCharityId, activeMembership, isReady, isPending, isError, error } = useActiveOrg();
  const { pending, errored, standings, read } = useCharityConsole();
  const { user } = useAuth();
  const moduleStanding = useMemo(
    () => complianceStandingFor({ pending, errored, standings }),
    [pending, errored, standings],
  );
  const orgRef = isReady ? activeCharityId : null;
  const reminders = useComplianceReminders(orgRef, moduleStanding === 'available');
  const roster = useOrgMembersExhausted(
    orgRef ?? '',
    orgRef !== null && moduleStanding === 'available',
  );
  const personName = useMemo(() => rosterNames(roster.data), [roster.data]);
  const members = useMemo(
    () => organizationMembers({ data: roster.data, isError: roster.isError }),
    [roster.data, roster.isError],
  );
  const complianceSigners = useMemo(() => complianceSignerCandidates(roster.data), [roster.data]);
  const authorizers = useMemo(() => complianceAuthorizers(roster.data), [roster.data]);
  const writers = useMemo(() => complianceWriters(roster.data), [roster.data]);
  // CCM-R1-SUBMIT-MCP: whether "Let your AI send this" may show — the viewer's own connected AI.
  const me = useQuery(usersGetMeOptions());
  const connectedAi = useConnectedAiRows(orgRef, moduleStanding === 'available');
  const aiCanSend = connectedAiCanSend(connectedAi.data, me.data?.id);
  const organizationName = activeMembership?.name?.trim() ?? '';
  const navigate = useNavigate();
  // "Invite someone to Compliance" leads to the module's own Team page, for a viewer who may invite.
  const mayInvite =
    read.permissions === null || read.permissions.includes(COMPLIANCE_INVITE_ADMINS_PERMISSION);
  const inviteToCompliance = useMemo(
    () => ({
      href: COMPLIANCE_ROUTES.team,
      open: () => void navigate({ to: COMPLIANCE_ROUTES.team }),
    }),
    [navigate],
  );
  const canSendForSignature = complianceCanSendForSignature(read.permissions);
  // CCM-R1-DESK-WEB: frame 21's "Go to settings" opens Settings as the gear does from here, with
  // Compliance's section first, where "Use AI on your organization’s data" is.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const complianceSettings = useMemo(
    () => ({
      href: settingsHref(pathname, COMPLIANCE_SLUG),
      open: () =>
        void navigate({
          to: SETTINGS_ROUTE,
          search: settingsOriginSearchFor(pathname, COMPLIANCE_SLUG),
        }),
    }),
    [pathname, navigate],
  );
  return (
    <ComplianceProvider
      host={{
        orgRef,
        isReady,
        isPending,
        isError,
        error,
        moduleStanding,
        reminders,
        canWrite: complianceCanWrite(read.permissions),
        personName,
        ...(members !== undefined ? { organizationMembers: members } : {}),
        ...(canSendForSignature !== undefined ? { canSendForSignature } : {}),
        ...(complianceSigners !== undefined ? { complianceSigners } : {}),
        ...(authorizers !== undefined ? { complianceAuthorizers: authorizers } : {}),
        ...(aiCanSend !== undefined ? { connectedAiCanSend: aiCanSend } : {}),
        ...(mayInvite ? { inviteToCompliance } : {}),
        complianceSettings,
        ...(user !== null ? { viewerKey: user.uid } : {}),
        // CCM-R1-REVIEW-ASSIGN: the viewer's own backend id, and who may be asked.
        ...(me.data?.id !== undefined ? { viewerUserId: me.data.id } : {}),
        ...(writers !== undefined ? { complianceWriters: writers } : {}),
        ...(organizationName.length > 0 ? { organizationName } : {}),
        // S10: Account Security's offer, as Board's Sign opens it (AS-WEB11, `from-sign`), and its
        // from-Send variant for Authorize and send (CCM-R1-SIGN-PASSKEY).
        renderPasskeySetupOffer: renderSetupOffer,
      }}
      operations={charityComplianceOperations}
    >
      {children}
    </ComplianceProvider>
  );
}
